import { db } from "../../main";
import { firestore } from "firebase/app";
import { ProjectDetails } from "@/data/models/ProjectDetails";
import {
  ProgramChain,
  getEndDateOfProgramChain,
  isProgramChainAssignedTo,
  matchesDeviceAssignmentDetails,
  matchesMinProgramChainDuration,
} from "../models/ProgramChain";
import { ProgramChainQualityRating } from "@/data/models/ProgramChainQualityRating";
import { RunOverview, isIdleRun, isActiveRun } from "../models/RunOverview";
import {
  createPanelSampleFromBase64,
  createComBoxSampleFromBase64,
  SampleInfo,
} from "../models/SampleInfo";
import { TimeRange } from "@/data/models/TimeRange";
import { ClientPanelAssignment } from "@/data/models/ClientPanelAssignment";
import { getClientId, getMemberClientIds } from "@/data/repository/ClientRepository";
import { adjustEndDate, areOnSameDay, toEndOfDay } from "@/utils/DateHelper";
import { updateRun } from "@/data/repository/RunRepository";
import { ProgramChainModule } from "@/store/modules/ProgramChainModule";
import _ from "lodash";
import {
  getIresDeviceCollectionName,
  getIresDeviceCategoryFromSerialNumber,
  IresDeviceCategory,
} from "../models/IresDevice";
import { filterOutUndefined } from "@/utils/Utils";
import { IresDeviceAssignment } from "../models/IresDeviceAssignment";
import {
  adminRightsAccess,
  projectManagementAccess,
  removeOutlierSamplesAccess,
  viewHyDryDeviceDetailsOfAllClientsAccess,
  viewHyDryDeviceDetailsOfMemberAccess,
} from "@/permissions/UserPermissions";
import { getMinProgramChainDurationInMin } from "./SettingsRepository";
import { getEffectiveUserLazily } from "@/utils/EffectiveUserLazy";

/**
 * A minimal snapshot shape used by the merged query wrapper.
 * Mirrors the Firestore snapshot fields used in this file.
 */
interface ProgramChainsQuerySnapshot {
  docs: Array<firestore.QueryDocumentSnapshot<object>>;
  size: number;
  empty: boolean;
}

/**
 * Minimal query contract used by this module.
 * This allows getProgramChainsRef to return either a single Firestore query
 * or a merged multi-query wrapper with the same call shape.
 */
interface ProgramChainsQuery {
  where(
    fieldPath: string,
    opStr: firebase.firestore.WhereFilterOp,
    value: unknown
  ): ProgramChainsQuery;
  orderBy(
    fieldPath: string,
    directionStr?: firebase.firestore.OrderByDirection
  ): ProgramChainsQuery;
  limit(limit: number): ProgramChainsQuery;
  startAfter(...fieldValues: unknown[]): ProgramChainsQuery;
  get(): Promise<ProgramChainsQuerySnapshot>;
  onSnapshot(
    onNext: (snapshot: ProgramChainsQuerySnapshot) => void
  ): () => void;
}

/**
 * Merges multiple Firestore queries into a single query-like object.
 *
 * Why this exists:
 * Firestore limits the `in` operator to at most 10 values. When a user can
 * access program chains for more than 10 client IDs, we must split the `in`
 * filter into chunks and combine the results.
 *
 * Behavior:
 * - Supports chaining `where`, `orderBy`, `limit`, and `startAfter`.
 * - `get()` merges all query snapshots, de-duplicates by document path,
 *   sorts using the last applied `orderBy`, and applies `limit` after merge.
 * - `onSnapshot()` subscribes to each chunked query and emits a merged
 *   snapshot whenever any chunk updates.
 *
 * Notes:
 * - Merged ordering is done in memory and assumes the orderBy field is
 *   comparable (e.g. Firestore Timestamp or primitive).
 * - If no `orderBy` is set, documents are returned in undefined order.
 */
class MergedProgramChainsQuery implements ProgramChainsQuery {
  private orderByField?: string;
  private orderByDirection?: firebase.firestore.OrderByDirection;
  private limitCount?: number;

  constructor(private readonly queries: Array<firestore.Query<object>>) {}

  where(
    fieldPath: string,
    opStr: firebase.firestore.WhereFilterOp,
    value: unknown
  ) {
    return new MergedProgramChainsQuery(
      this.queries.map((query) => query.where(fieldPath, opStr, value))
    ).withOrderBy(this.orderByField, this.orderByDirection)
      .withLimit(this.limitCount);
  }

  orderBy(
    fieldPath: string,
    directionStr?: firebase.firestore.OrderByDirection
  ) {
    return new MergedProgramChainsQuery(
      this.queries.map((query) => query.orderBy(fieldPath, directionStr))
    ).withOrderBy(fieldPath, directionStr)
      .withLimit(this.limitCount);
  }

  limit(limit: number) {
    return new MergedProgramChainsQuery(
      this.queries.map((query) => query.limit(limit))
    ).withOrderBy(this.orderByField, this.orderByDirection)
      .withLimit(limit);
  }

  startAfter(...fieldValues: unknown[]) {
    return new MergedProgramChainsQuery(
      this.queries.map((query) => query.startAfter(...fieldValues))
    ).withOrderBy(this.orderByField, this.orderByDirection)
      .withLimit(this.limitCount);
  }

  async get(): Promise<ProgramChainsQuerySnapshot> {
    if (this.queries.length === 1) {
      const snapshot = await this.queries[0].get();
      return {
        docs: snapshot.docs,
        size: snapshot.size,
        empty: snapshot.empty,
      };
    }

    const snapshots = await Promise.all(
      this.queries.map((query) => query.get())
    );
    return this.mergeSnapshots(snapshots);
  }

  onSnapshot(
    onNext: (snapshot: ProgramChainsQuerySnapshot) => void
  ): () => void {
    if (this.queries.length === 1) {
      return this.queries[0].onSnapshot((snapshot) =>
        onNext({
          docs: snapshot.docs,
          size: snapshot.size,
          empty: snapshot.empty,
        })
      );
    }

    const snapshotsByIndex = new Map<
      number,
      firebase.firestore.QuerySnapshot<object>
    >();

    const unsubscribeFns = this.queries.map((query, index) =>
      query.onSnapshot((snapshot) => {
        snapshotsByIndex.set(index, snapshot);

        // Nothing is reported until every chunk has answered once. A merged result
        // that is ordered and limited is only meaningful over the complete set:
        // reporting a partial merge means the consumer picks, say, the newest chain
        // among the first chunk's documents and then has to switch when a later chunk
        // delivers an older-indexed but genuinely newer one.
        if (snapshotsByIndex.size < this.queries.length) {
          return;
        }

        onNext(this.mergeSnapshots(Array.from(snapshotsByIndex.values())));
      })
    );

    return () => {
      unsubscribeFns.forEach((fn) => fn());
    };
  }

  private withOrderBy(
    field?: string,
    direction?: firebase.firestore.OrderByDirection
  ) {
    this.orderByField = field;
    this.orderByDirection = direction;
    return this;
  }

  private withLimit(limit?: number) {
    this.limitCount = limit;
    return this;
  }

  private mergeSnapshots(
    snapshots: Array<firebase.firestore.QuerySnapshot<object>>
  ): ProgramChainsQuerySnapshot {
    const docsByPath = new Map<string, firestore.QueryDocumentSnapshot<object>>();

    snapshots.forEach((snapshot) => {
      snapshot.docs.forEach((doc) => {
        docsByPath.set(doc.ref.path, doc);
      });
    });

    let docs = Array.from(docsByPath.values());

    if (this.orderByField) {
      const direction = this.orderByDirection === "desc" ? -1 : 1;
      const field = this.orderByField;

      docs = docs.sort((d1, d2) => {
        const v1 = d1.get(field);
        const v2 = d2.get(field);

        const n1 =
          v1 && typeof v1 === "object" && "seconds" in v1
            ? v1.seconds
            : v1;
        const n2 =
          v2 && typeof v2 === "object" && "seconds" in v2
            ? v2.seconds
            : v2;

        if (n1 === n2) {
          return 0;
        }

        return n1 > n2 ? direction : -direction;
      });
    }

    if (this.limitCount !== undefined) {
      docs = docs.slice(0, this.limitCount);
    }

    return {
      docs,
      size: docs.length,
      empty: docs.length === 0,
    };
  }
}

export function getProgramChainRef(
  serialNumber: string,
  programChainId: string
) {
  return db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("chains")
    .doc(programChainId);
}

/**
 * Returns a program-chains query scoped to the current user's access.
 *
 * Firestore limitation:
 * - `in` queries support a maximum of 10 values. If the active user can
 *   access more than 10 member clients, we split the list into chunks of 10
 *   and merge the results in memory via `MergedProgramChainsQuery`.
 *
 * This function returns a query-like object that supports `where`, `orderBy`,
 * `limit`, `startAfter`, `get`, and `onSnapshot`, regardless of whether the
 * underlying data source is a single query or multiple chunked queries.
 */
function getProgramChainsRef(
  serialNumber: string,
  alwaysScopeToClient?: boolean
) {
  const ref = db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("chains");

  // Whose chains: the chosen user's while "Sicht auf" is active (ticket 2610052).
  if (viewHyDryDeviceDetailsOfAllClientsAccess(getEffectiveUserLazily()) && alwaysScopeToClient !== true) {
    return new MergedProgramChainsQuery([ref]);
  } else {
    const activeUser = getEffectiveUserLazily();
    const memberClientIds = getMemberClientIds();

    if (viewHyDryDeviceDetailsOfMemberAccess(activeUser) && memberClientIds.length > 0) {
      const chunks = _.chunk(memberClientIds, 10);
      const queries = chunks.map((chunk) =>
        ref.where("assignedClientId", "in", chunk)
      );
      return new MergedProgramChainsQuery(queries);
    } else {
      const clientId = getClientId();
      return new MergedProgramChainsQuery([
        ref.where("assignedClientId", "==", clientId),
      ]);
    }
  }
}

async function updateProgramChain(
  serialNumber: string,
  chainId: string,
  update: object
) {
  return db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("chains")
    .doc(chainId)
    .set(update, { merge: true });
}

export interface ProgramChainLoadResult {
  canLoadMore: boolean;
  nextPaginationDate: Date | undefined;
  programChains: Array<ProgramChain>;
}

function extractSampleInfo(
  runOverview: RunOverview,
  iresDeviceCategory: IresDeviceCategory,
  isRunOfEdgePanel: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sampleData: any,
  removeOutlierSamples: boolean
) {
  if (!sampleData) {
    runOverview.samples = [];
    return;
  }

  const isActiveRun = !isIdleRun(runOverview);
  const sampleIndices = Object.keys(sampleData);
  const samples: Array<SampleInfo> = [];

  for (const sampleIndex of sampleIndices) {
    runOverview.durationInMin;

    const sample =
      iresDeviceCategory === IresDeviceCategory.PANEL
        ? createPanelSampleFromBase64(
            sampleData[sampleIndex],
            Number(sampleIndex),
            isRunOfEdgePanel,
            isActiveRun
          )
        : createComBoxSampleFromBase64(
            sampleData[sampleIndex],
            runOverview,
            removeOutlierSamples
          );

    if (sample) {
      samples.push(sample);
    }
  }

  runOverview.samples = samples.sort(
    (s1, s2) => s1.sampleIndex - s2.sampleIndex
  );
}

/**
 * Deserializes a program chain document.
 *
 * `includeSamples: false` skips the per-sample base64 decoding, which is the bulk of
 * the cost: a multi-week drying run holds thousands of samples and each one is decoded
 * and interpreted individually. Use it when only chain and run metadata is needed -
 * dates, durations, assignment - and never store the result anywhere a consumer could
 * mistake it for a full chain, because its runs carry no samples.
 */
function extractProgramChainFromDoc(
  serialNumber: string,
  doc: firestore.QueryDocumentSnapshot<object>,
  includeSamples = true
) {
  const isEdgePanel = serialNumber.toUpperCase().startsWith("E");

  const runMap = doc.get("runs");
  const runs = [];

  let removeOutlierSamples = false;

  if (includeSamples) {
    const activeUser = getEffectiveUserLazily();

    if (removeOutlierSamplesAccess(activeUser)) {
      removeOutlierSamples =
        !adminRightsAccess(activeUser) &&
        !projectManagementAccess(activeUser);
    } else {
      removeOutlierSamples = true;
    }
  }

  const chainId = doc.get("id");
  const runIds = Object.keys(runMap);
  const iresDeviceCategory = getIresDeviceCategoryFromSerialNumber(
    serialNumber
  );

  for (const runId of runIds) {
    const runOverview = runMap[runId] as RunOverview;

    runOverview.id = runId;
    runOverview.chainId = chainId;

    if (runOverview.assignedProjectId && runOverview.assignedProjectDetails) {
      runOverview.projectAssignmentDetails = {
        projectId: runOverview.assignedProjectId,
        ...runOverview.assignedProjectDetails,
      };
    }

    if (includeSamples) {
      extractSampleInfo(
        runOverview,
        iresDeviceCategory,
        isEdgePanel,
        runMap[runId]["samples"],
        removeOutlierSamples
      );
    }

    runs.push(runOverview);
  }

  const sortedRuns = runs.sort(
    (r1, r2) => r1.startDate.seconds - r2.startDate.seconds
  );

  sortedRuns.forEach((run, index) => (run.numberInChain = index + 1));

  return {
    startDate: doc.get("startDate"),
    serialNumber: serialNumber,
    chainNumber: doc.get("chainNumber"),
    id: chainId,
    numNotes: doc.get("numNotes"),

    assignedClientId: doc.get("assignedClientId"),
    assignedProjectId: doc.get("assignedProjectId"),
    assignedProjectDetails: doc.get("assignedProjectDetails"),
    assignmentId: doc.get("assignmentId"),

    rating: doc.get("rating"),

    runs: sortedRuns,
  };
}

function convert(
  serialNumber: string,
  limit: number,
  querySnapshot: ProgramChainsQuerySnapshot
): ProgramChainLoadResult {
  const programChains = querySnapshot.docs.map((doc) =>
    extractProgramChainFromDoc(serialNumber, doc)
  );

  const nextPaginationDate =
    programChains.length === 0
      ? undefined
      : programChains[programChains.length - 1].startDate.toDate();

  return {
    canLoadMore: querySnapshot.size == limit,
    programChains: programChains,
    nextPaginationDate: nextPaginationDate,
  };
}

export function observeProgramChain(
  serialNumber: string,
  chainId: string,
  onNext: (programChain: ProgramChain) => void
) {
  return getProgramChainsRef(serialNumber)
    .where("id", "==", chainId)
    .limit(1)
    .onSnapshot((result) => {
      const data = result.docs[0];

      if (data) {
        const programChain = extractProgramChainFromDoc(serialNumber, data);
        onNext(programChain);
      }
    });
}

export function observeMostRecentProgramChain(
  serialNumber: string,
  onNext: (programChain: ProgramChain) => void,
  ignoreCurrentAssignmentIfIresClient?: boolean
) {
  const mostRecentProgramChain = ProgramChainModule.mostRecentProgramChainCache.get(
    serialNumber
  );

  if (mostRecentProgramChain) {
    onNext(mostRecentProgramChain);
  }

  return getProgramChainsRef(serialNumber, !ignoreCurrentAssignmentIfIresClient)
    .orderBy("startDate", "desc")
    .limit(1)
    .onSnapshot((result) => {
      const data = result.docs[0];

      if (data) {
        const programChain = extractProgramChainFromDoc(serialNumber, data);

        ProgramChainModule.cacheMostRecentProgramChain(programChain);

        onNext(programChain);
      } else {
        ProgramChainModule.clearMostRecentProgramChain(serialNumber);
      }
    });
}

/**
 * How many of a device's newest program chains are scanned when looking for the most
 * recent one that belongs to this client.
 *
 * Every row of the hyDry device list runs that scan, and the samples of a drying run are
 * stored inside the chain document, so an unbounded scan downloads the device's entire
 * drying history. Measured on a 353-device list: scanning everything kept the main
 * thread blocked for 19.2 s of a scroll through the list, almost all of it inside
 * Firestore's own parsing, against 6.4 s with this bound.
 *
 * The trade-off, accepted deliberately: a device whose five newest chains all belong to
 * other clients shows no drying information in its row. That needs five consecutive
 * foreign chains, which only happens on devices that move between clients constantly.
 * The detail screens are unaffected - they read chains through their own queries.
 */
const MAX_SCANNED_PROGRAM_CHAINS = 5;

/* 
Due to the movement of IRES devices from one client to another (as in the case of BELFOR, for example), an issue arose where program chains were 
incorrectly assigned to the wrong client. We need to implement a filter to ensure they don't appear under the incorrect client. 
*/
export function observeMostRecentProgramChainBelongsToClient(
  serialNumber: string,
  iresDeviceAssignments: IresDeviceAssignment[],
  onNext: (programChain: ProgramChain) => void,
  isAllowedToViewProgramChainsOfAllClients?: boolean
) {
  const minProgramChainDuration = getMinProgramChainDurationInMin();
  const mostRecentProgramChain = ProgramChainModule.mostRecentProgramChainCache.get(
    serialNumber
  );
  
  if (mostRecentProgramChain) {
    if (
      matchesMinProgramChainDuration(mostRecentProgramChain, minProgramChainDuration) &&
      (
        matchesDeviceAssignmentDetails(mostRecentProgramChain, iresDeviceAssignments) ||
        isAllowedToViewProgramChainsOfAllClients
      )
    ) {
      onNext(mostRecentProgramChain);
    }
  }

  return getProgramChainsRef(serialNumber, !isAllowedToViewProgramChainsOfAllClients)
    .orderBy("startDate", "desc")
    .limit(MAX_SCANNED_PROGRAM_CHAINS)
    .onSnapshot((result) => {
      const docs = result.docs;

      for (const data of docs) {
        if (data) {
          // Both predicates read run dates, durations and the active-run flag only, so
          // the candidate is deserialized without decoding its samples. Every device
          // row in the hyDry list runs this scan over the device's whole chain
          // history, and a single multi-week chain holds thousands of samples: decoding
          // them for chains that are then discarded was the dominant cost of opening
          // the list.
          const candidate = extractProgramChainFromDoc(serialNumber, data, false);

          if (
            matchesMinProgramChainDuration(candidate, minProgramChainDuration) &&
            (
              matchesDeviceAssignmentDetails(candidate, iresDeviceAssignments) ||
              isAllowedToViewProgramChainsOfAllClients
            )
          ) {
            // Only the match is deserialized in full. The cache is shared with the
            // detail screens, so a sample-less chain must never be written into it.
            const programChain = extractProgramChainFromDoc(serialNumber, data);

            ProgramChainModule.cacheMostRecentProgramChain(programChain);
            onNext(programChain);
            break;
          }

        } else {
          ProgramChainModule.clearMostRecentProgramChain(serialNumber);
        }
      }
    });
}

export async function observeRecentProgramChains(
  serialNumber: string,
  onNext: (programChains: Array<ProgramChain>) => void
) {
  const mostRecent = await getProgramChainsRef(serialNumber)
    .orderBy("startDate", "desc")
    .limit(1)
    .get();

  let startDate = new Date();

  if (mostRecent && !mostRecent.empty) {
    const { programChains } = convert(serialNumber, 0, mostRecent);
    startDate = programChains[0].startDate.toDate();
  }

  const ref = getProgramChainsRef(serialNumber)
    .orderBy("startDate", "desc")
    .where("startDate", ">=", startDate);

  return {
    startDate: startDate,
    subscription: ref.onSnapshot((snapshot) => {
      const { programChains } = convert(serialNumber, 0, snapshot);
      onNext(programChains);
    }),
  };
}

export async function loadProgramChains(
  serialNumber: string,
  limit: number,
  startDate?: Date
): Promise<ProgramChainLoadResult> {
  const ref = getProgramChainsRef(serialNumber).orderBy("startDate", "desc");

  if (startDate) {
    return convert(
      serialNumber,
      limit,
      await ref
        .startAfter(startDate)
        .limit(limit)
        .get()
    );
  } else {
    return convert(serialNumber, limit, await ref.limit(limit).get());
  }
}

interface GetTimeRangeQueryOptions {
  limit?: number;
  projectId?: string;
  alwaysScopeToClient?: boolean;
}

function getTimeRangeQuery(
  serialNumber: string,
  timeRange: TimeRange,
  getTimeRangeQueryOptions?: GetTimeRangeQueryOptions
) {
  const { limit, projectId, alwaysScopeToClient } =
    getTimeRangeQueryOptions ?? {};

  const startDate = timeRange.startDate.toDate();

  const endDate = timeRange.endDate;
  const isOpenEndedTimeRange = !endDate;

  const query = getProgramChainsRef(serialNumber, alwaysScopeToClient);

  const adjustedEndDate = adjustEndDate(endDate);

  if (isOpenEndedTimeRange) {
    const result = query
      .where("startDate", ">=", startDate)
      .orderBy("startDate", "desc");

    if (limit) {
      return result.limit(limit);
    } else {
      return result;
    }
  } else {
    const result = query
      .where("startDate", ">=", startDate)
      .where("startDate", "<=", adjustedEndDate)
      .orderBy("startDate", "desc");

    if (limit) {
      if (projectId) {
        return query
          .where("assignedProjectId", "==", projectId)
          .where("startDate", ">=", startDate)
          .where("startDate", "<=", adjustedEndDate)
          .orderBy("startDate", "desc")
          .limit(limit);
      } else {
        return result.limit(limit);
      }
    } else {
      return result;
    }
  }
}

async function observeProgramChainsInOpenTimeRange(
  serialNumber: string,
  projectId: string,
  timeRange: TimeRange,
  onNext: (programChains: Array<ProgramChain>) => void,
  limit?: number
) {
  const query = getTimeRangeQuery(serialNumber, timeRange, {
    limit: limit,
    alwaysScopeToClient: !!projectId,
  });

  return query.onSnapshot((snapshot) => {
    const programChains = snapshot.docs
      .map((doc) => extractProgramChainFromDoc(serialNumber, doc))
      .filter((programChain) => programChain.runs.length > 0)
      .filter((programChain) => {
        if (!timeRange.id) {
          return true;
        }

        if (!programChain.assignedProjectDetails?.assignmentId) {
          return true;
        }

        return (
          programChain.assignedProjectDetails?.assignmentId == timeRange.id
        );
      });

    onNext(programChains);
  });
}

interface LoadProgramChainsInClosedTimeRangeOptions {
  projectId?: string;
  limit?: number;
  ignoreAssignmentInclusion?: boolean;
}

export async function loadProgramChainsInClosedTimeRange(
  serialNumber: string,
  timeRange: TimeRange,
  loadProgramChainsInClosedTimeRangeOptions?: LoadProgramChainsInClosedTimeRangeOptions
): Promise<Array<ProgramChain>> {
  const { limit, projectId, ignoreAssignmentInclusion } =
    loadProgramChainsInClosedTimeRangeOptions ?? {};

  const query = getTimeRangeQuery(serialNumber, timeRange, {
    limit: limit,
    projectId: projectId,
  });

  const result = await query.get();

  const programChains = result.docs
    .map((doc) => extractProgramChainFromDoc(serialNumber, doc))
    .filter(
      (programChain) =>
        programChain.runs.length > 0 &&
        (ignoreAssignmentInclusion ||
          programChain.assignedProjectId === projectId)
    )
    .filter((programChain) => {
      if (timeRange.id) {
        const some =
          ignoreAssignmentInclusion ||
          isProgramChainAssignedTo(programChain, timeRange.id);

        return some;
      } else {
        return true;
      }
    });

  return programChains;
}

export async function observeProgramChainsInTimeRanges(
  serialNumber: string,
  projectId: string,
  timeRanges: Array<TimeRange>,
  onNext: (programChains: Array<ProgramChain>) => void,
  limit?: number
) {
  const closedTimeRanges = timeRanges.filter(
    (timeRange) => timeRange.endDate !== null
  );

  const openTimeRanges = timeRanges.filter(
    (timeRange) => timeRange.endDate === null
  );

  const programChainsOfClosedTimeRanges = _.flatten(
    await Promise.all(
      closedTimeRanges.map((timeRange) =>
        loadProgramChainsInClosedTimeRange(serialNumber, timeRange, {
          projectId: projectId,
        })
      )
    )
  ).sort((p1, p2) => p2.startDate.toMillis() - p1.startDate.toMillis());

  if (openTimeRanges.length === 0) {
    onNext(programChainsOfClosedTimeRanges);
  } else if (openTimeRanges.length > 1) {
    throw Error("More than one open ended time range");
  } else {
    const openTimeRange = openTimeRanges[0];

    return await observeProgramChainsInOpenTimeRange(
      serialNumber,
      projectId,
      openTimeRange,
      (programChains: Array<ProgramChain>) => {
        const mergedProgramChains = [
          ...programChains,
          ...programChainsOfClosedTimeRanges,
        ].sort((p1, p2) => p2.startDate.toMillis() - p1.startDate.toMillis());

        onNext(mergedProgramChains);
      },
      limit
    );
  }

  return null;
}

export async function assignProgramChainsToProject(
  clientPanelAssignment: ClientPanelAssignment,
  programChains: ProgramChain[],
  project: ProjectDetails
) {
  const {
    serialNumber,
    assignedProjectId,
    assignedProjectName,
  } = clientPanelAssignment;

  const runsOfProgramChains = _.flatMap(
    programChains,
    (programChain: ProgramChain) => programChain.runs
  );

  const doesProjectHaveMultipleContactPersons =
    project.clientData.contactPersons.length > 1;

  const timeRange = {
    startDate: clientPanelAssignment.assignmentStartDate,
    endDate: clientPanelAssignment.assignmentEndDate,
  };

  const clientId = getClientId();

  const contactPerson = doesProjectHaveMultipleContactPersons
    ? project.clientData.contactPersons.find(
        (contactPerson) =>
          contactPerson.id === clientPanelAssignment.contactPersonId
      )
    : undefined;

  const assignedProjectDetails = filterOutUndefined({
    projectName: assignedProjectName,
    startDate: timeRange.startDate,
    endDate: timeRange.endDate,
    clientId: clientId,
    assignmentId: clientPanelAssignment.id,
    contactPersonNumber: contactPerson?.contactPersonNumber,
    contactPersonName: contactPerson?.lastName,
  });

  return Promise.all([
    ...programChains.map((programChain) =>
      updateProgramChain(serialNumber, programChain.id, {
        assignedProjectId: assignedProjectId,
        assignedProjectDetails: assignedProjectDetails,
      })
    ),
    ...runsOfProgramChains.map((runOverview) =>
      updateRun(serialNumber, runOverview.id, {
        assignedProjectId: assignedProjectId,
        blub: 1,
        assignedProjectDetails: assignedProjectDetails,
      })
    ),
  ]);
}

export async function assignRunsInTimeRangeToProject(
  clientPanelAssignment: ClientPanelAssignment,
  project: ProjectDetails
) {
  const { serialNumber } = clientPanelAssignment;

  const timeRange = {
    startDate: clientPanelAssignment.assignmentStartDate,
    endDate: clientPanelAssignment.assignmentEndDate,
  };

  const programChains = await loadProgramChainsInClosedTimeRange(
    serialNumber,
    timeRange
  );

  const maxEndDate = clientPanelAssignment.assignmentEndDate
    ? toEndOfDay(clientPanelAssignment.assignmentEndDate).toMillis()
    : 0;

  const finishedProgramChains = programChains.filter((chain) => {
    if (getEndDateOfProgramChain(chain).toMillis() > maxEndDate) {
      return false;
    }

    if (
      areOnSameDay(chain.startDate, clientPanelAssignment.assignmentEndDate)
    ) {
      //Chain is completed
      return !chain.runs.some((run) => isActiveRun(run));
    } else {
      //Chain was not started on assignment end day
      return true;
    }
  });

  return assignProgramChainsToProject(
    clientPanelAssignment,
    finishedProgramChains,
    project
  );
}

export async function getUnassignedProgramChainsInOpenTimeRange(
  serialNumber: string,
  assignmentStartDate: firestore.Timestamp,
  assignmentId?: string
) {
  const programChains = (
    await loadProgramChainsInClosedTimeRange(
      serialNumber,
      {
        startDate: assignmentStartDate,
        endDate: toEndOfDay(firestore.Timestamp.fromDate(new Date())),
      },
      {
        ignoreAssignmentInclusion: true,
      }
    )
  ).reverse();

  if (programChains.length === 0) {
    return {
      allProgramChains: programChains,
      filteredProgramChains: programChains,
    };
  }

  const filteredProgramChains = [];

  for (let i = 0; i < programChains.length; i++) {
    const programChain = programChains[i];

    if (
      programChain.assignedProjectId &&
      (!assignmentId ||
        programChain.assignedProjectDetails?.assignmentId !== assignmentId)
    ) {
      if (
        areOnSameDay(
          getEndDateOfProgramChain(programChain),
          assignmentStartDate
        )
      ) {
        continue;
      }

      break;
    }

    filteredProgramChains.push(programChain);
  }

  return {
    allProgramChains: programChains,
    filteredProgramChains: filteredProgramChains,
  };
}

export async function getProgramChainsOfClientPanelAssignment(
  clientPanelAssignment: ClientPanelAssignment
): Promise<ProgramChain[]> {
  const serialNumber = clientPanelAssignment.serialNumber;
  if (clientPanelAssignment.assignmentEndDate) {
    const result = await getProgramChainsRef(serialNumber, true)
      .where("assignmentId", "==", clientPanelAssignment.id)
      .get();

    if (result.empty) {
      return [];
    }

    return result.docs
      .map((doc) => extractProgramChainFromDoc(serialNumber, doc))
      .sort((p1, p2) => p1.chainNumber - p2.chainNumber);
  } else {
    return (
      await getUnassignedProgramChainsInOpenTimeRange(
        serialNumber,
        clientPanelAssignment.assignmentStartDate,
        clientPanelAssignment.id
      )
    ).filteredProgramChains;
  }
}

export async function getProgramChainsInClosedTimeRange(
  assignmentId: string,
  serialNumber: string,
  assignmentStartDate: firestore.Timestamp,
  assignmentEndDate: firestore.Timestamp
) {
  if (!assignmentEndDate) {
    return [];
  }

  const programChains = (
    await loadProgramChainsInClosedTimeRange(
      serialNumber,
      {
        startDate: assignmentStartDate,
        endDate: assignmentEndDate,
      },
      { ignoreAssignmentInclusion: true }
    )
  ).reverse();

  return programChains.filter((programChain) => {
    if (!programChain.assignedProjectDetails?.assignmentId) {
      return true;
    }

    return programChain.assignedProjectDetails.assignmentId == assignmentId;
  });
}

export async function getFirstProgramChainAfter(
  serialNumber: string,
  date: firebase.firestore.Timestamp,
  assignmentId?: string
): Promise<ProgramChain | null> {
  const programChains = await loadProgramChainsInClosedTimeRange(serialNumber, {
    id: assignmentId,
    startDate: date,
    endDate: firestore.Timestamp.fromDate(new Date()),
  });

  return programChains.reverse()[0] ?? null;
}

export async function getFirstProgramChainStartedBefore(
  serialNumber: string,
  date: firestore.Timestamp
): Promise<ProgramChain | null> {
  const result = await getProgramChainsRef(serialNumber, true)
    .orderBy("startDate", "desc")
    .where("startDate", "<", date)
    .limit(1)
    .get();

  if (result.empty) {
    return null;
  }

  return extractProgramChainFromDoc(serialNumber, result.docs[0]);
}

export async function getMostRecentProgramChain(serialNumber: string) {
  const result = await getProgramChainsRef(serialNumber)
    .orderBy("startDate", "desc")
    .limit(1)
    .get();

  if (result.empty) {
    return null;
  }

  return extractProgramChainFromDoc(serialNumber, result.docs[0]);
}

export async function getProgramChain(
  serialNumber: string,
  chainId: string
): Promise<ProgramChain> {
  const result = await getProgramChainsRef(serialNumber, false)
    .where("id", "==", chainId)
    .get();

  return extractProgramChainFromDoc(serialNumber, result.docs[0]);
}

export async function setQualityRatingOfProgramChain(
  serialNumber: string,
  chainId: string,
  programChainQualityRating: ProgramChainQualityRating
) {
  updateProgramChain(serialNumber, chainId, {
    rating: filterOutUndefined(programChainQualityRating),
  });
}
